import { pickBy } from "lodash";
import moment from "moment";
import { createFormDataFromJSON, validateMedia, validationForThumbnail } from "utils/HelperFunctions";
import * as yup from "yup";

const MementoSchema = yup.object().shape({
  name: yup.string().max(100, "Only 100 characters are allowed for Memento name").trim().required("* Required field"),
  thumbnail: yup.mixed().when("thumbnail_url", {
    is: (thumbnail_url: string) => !thumbnail_url,
    then: validationForThumbnail,
  }),
  thumbnail_url: yup.string(),
  start_time: yup.date().required("* Required field").typeError("Invalid start date"),
  description: yup
    .string()
    .max(1000, "Description should be within 1000 characters")
    .trim()
    .required("* Required field"),
  is_incentive: yup.bool(),
  incentive_rules: yup.mixed().when("is_incentive", {
    is: true,
    then: () =>
      yup
        .object()
        .shape({
          operator: yup
            .string()
            .test("Valid Rule", "Add atleast one valid rule", async (value: string | undefined, context) => {
              if (value === "AND" || value === "OR") {
                return true;
              }
              return context.createError({
                path: "incentive_rules",
                message: "Please add atleast one valid Rule",
              });
            }),
          conditions: yup.array().of(
            yup.object().shape({
              action_type: yup.string().required(),
              value: yup.mixed().when("action_type", {
                is: "MIN_PARTICIPATED_COUNT",
                then: () => yup.number().min(1, "Please choose a value more than 0").required(),
                otherwise: () => yup.array().min(1, "Choose atleast one memento").required(),
              }),
              activation_key: yup.string(),
            }),
          ),
        })
        .required("Add Valid Rules"),
    otherwise: yup.mixed().nullable(),
  }),
  end_time: yup
    .date()
    .typeError("Invalid expiry date")
    .min(yup.ref("start_time"), "Expiry date should greater than start date")
    .nullable()
    .when("is_incentive", {
      is: true,
      then: (schema) => schema.required("* Required field"),
    }),
});

const AirdropSchema = MementoSchema.shape({
  media_url: yup.string().nullable(),
  media_url_new: yup.string().nullable(),
  media: yup.mixed().when(["is_gated", "media_url", "media_url_new"], {
    is: function isGated(is_gated: boolean, media_url: string, media_url_new: string) {
      if (is_gated) return !(media_url || media_url_new);
      return false;
    },
    then: function then(schema) {
      return schema
        .test("file-validation", "'File validation failed.'", (value, context) => {
          const validationError = validateMedia(value);
          if (validationError) {
            return context.createError({ message: validationError });
          }
          return true;
        })
        .required(
          "'Exclusive Content' checkbox is ticked, please add content which needs to be exclusive OR untick the 'Exclusive Content' checkbox.",
        );
    },
    otherwise: function otherwise(_schema) {
      return _schema.nullable();
    },
  }),
  is_gated: yup.bool(),
});

const ClaimCodeSchema = MementoSchema.shape({
  media_url: yup.string(),
  media_url_new: yup.string(),
  media: yup.mixed().when(["is_gated", "media_url", "media_url_new"], {
    is: (is_gated: boolean, media_url: string, media_url_new: string) => {
      if (is_gated) return !(media_url || media_url_new);
      return false;
    },
    then: (schema) => schema.required("Please select a valid CSV file"),
    otherwise: (_schema) => _schema.required("Please select a valid CSV file"),
  }),
  claim_instructions: yup.string().trim().required("* Required field").nullable().max(1000),
});

const MerchDropSchema = MementoSchema.shape({
  options: yup.array().of(
    yup.object().shape({
      name: yup.string().max(250, "Only 250 characters are allowed.").required("* Required field"),
      choices: yup
        .array()
        .of(
          yup
            .string()
            .test("Choices Character Limit", (data: string | undefined, context) => {
              if (data && data.length > 250) {
                return context.createError({
                  message: "Each choice must be shorter than 250 characters",
                  path: context.path.substring(0, context.path.lastIndexOf("[")),
                });
              }
              return true;
            })
            .required("* Required field"),
        )
        .min(2, "Please provide atleast 2 choices"),
    }),
  ),
  end_time: yup
    .date()
    .typeError("* Required field")
    .min(yup.ref("start_time"), "Expiry date should greater than start date")
    .required("* Required field"),
});

export const CreateMementoSchema = {
  mem_drop: AirdropSchema,
  claim_code: ClaimCodeSchema,
  merch_drop: MerchDropSchema,
};

export const getDefaultValues = (mementoData: any, activationKey: ActivationsKeys, magnetMementos: Memento[]) => {
  const defaultValues: any = {
    name: mementoData ? mementoData.name : "",
    description: mementoData ? mementoData?.description : "",
    start_time: mementoData ? moment(new Date(mementoData.start_time)) : moment(),
    auto_drop_on_purchase: mementoData ? mementoData.auto_drop_on_purchase : true,
    is_incentive: mementoData ? mementoData.is_incentive : false,
    end_time: null,
  };

  if (defaultValues.is_incentive) {
    defaultValues.end_time = mementoData ? moment(new Date(mementoData.end_time)) : null;

    if (mementoData && magnetMementos?.length) {
      const conditionsPayload = mementoData.incentive_rules?.conditions.map((condition: IncentiveCondition) => {
        const conditionPayload = {} as any;

        if (condition.action_type === "PARTICIPATED_IN") {
          conditionPayload.value = condition.value.map(
            // @ts-ignore
            (mementoID: string) => magnetMementos.filter((memento) => memento.id === mementoID)[0],
          );
        } else {
          conditionPayload.value = condition.value;
          conditionPayload.activation_key = condition.activation_key;
        }
        conditionPayload.action_type = condition.action_type;
        return conditionPayload;
      });
      defaultValues.incentive_rules = { operator: mementoData.incentive_rules.operator, conditions: conditionsPayload };
    } else {
      defaultValues.incentive_rules = {
        conditions: [{ activation_key: "poll", value: 1, action_type: "MIN_PARTICIPATED_COUNT" }],
      };
    }
  }

  switch (activationKey) {
    case "claim_code":
      defaultValues.claim_instructions = mementoData ? mementoData.claim_instructions : "";
      defaultValues.media = undefined;
      defaultValues.media_type = mementoData ? mementoData.file_url || "text/csv" : undefined;
      defaultValues.media_url = mementoData ? mementoData.file_url : undefined;
      defaultValues.thumbnail = undefined;
      defaultValues.thumbnail_url = mementoData ? mementoData.image_url : undefined;
      defaultValues.end_time = mementoData && mementoData.end_time ? moment(new Date(mementoData.end_time)) : null;
      break;
    case "mem_drop":
      defaultValues.media = undefined;
      defaultValues.media_type = mementoData ? mementoData.media_type : undefined;
      defaultValues.media_url = mementoData ? mementoData.media_url : undefined;
      defaultValues.is_gated = mementoData ? mementoData.is_gated : true;
      defaultValues.thumbnail = undefined;
      defaultValues.thumbnail_url = mementoData ? mementoData.thumbnail_url : undefined;
      defaultValues.end_time = mementoData && mementoData.end_time ? moment(new Date(mementoData.end_time)) : null;
      break;
    case "merch_drop":
      defaultValues.thumbnail = undefined;
      defaultValues.thumbnail_url = mementoData ? mementoData.image_url : undefined;
      defaultValues.options = mementoData ? mementoData.options : [{ name: "", choices: [] }];
      defaultValues.end_time = mementoData && mementoData.end_time ? moment(new Date(mementoData.end_time)) : null;
      break;
    default:
  }
  return defaultValues;
};

export const getPaylaodValues = (formData: MementoFormType, activationKey: ActivationsKeys) => {
  const payloadValues: any = {
    name: formData.name,
    description: formData.description,
    start_time: moment(formData.start_time).valueOf(),
    auto_drop_on_purchase: formData.auto_drop_on_purchase,
    is_incentive: formData.is_incentive,
  };

  if (formData.is_incentive) {
    payloadValues.end_time = moment(formData.end_time).valueOf();
    const rules = {} as any;
    const conditionsPayload = formData.incentive_rules?.conditions.map((condition) => {
      const conditionPayload = {} as any;

      if (condition.action_type === "PARTICIPATED_IN") {
        conditionPayload.value = condition.value.map((memento) => memento.id);
      } else {
        conditionPayload.value = condition.value;
        conditionPayload.activation_key = condition.activation_key;
      }
      conditionPayload.action_type = condition.action_type;
      return conditionPayload;
    });
    rules.conditions = conditionsPayload;
    rules.operator = formData.incentive_rules?.operator;
    payloadValues.incentive_rules = rules;
    payloadValues.auto_drop_on_purchase = false;
  }

  switch (activationKey) {
    case "claim_code":
      payloadValues.claim_instructions = formData.claim_instructions;
      payloadValues.file = formData.media;
      payloadValues.image_url = formData.thumbnail_url;
      payloadValues.end_time = moment(formData.end_time).valueOf();
      if (formData.is_incentive) {
        payloadValues.incentive_rules = JSON.stringify(payloadValues.incentive_rules);
      }
      return createFormDataFromJSON(pickBy(payloadValues, (v) => v !== undefined));
    case "mem_drop":
      payloadValues.media_type = formData.media_type;
      payloadValues.media_url = formData.media_url;
      payloadValues.is_gated = formData.is_gated;
      payloadValues.thumbnail_url = formData.thumbnail_url;
      break;
    case "merch_drop":
      payloadValues.image_url = formData.thumbnail_url;
      payloadValues.options = formData.options.map((option, idx) => ({ ...option, id: idx + 1 }));
      payloadValues.end_time = moment(formData.end_time).valueOf();
      break;
    default:
  }
  return payloadValues;
};

export const createClaimResponseFormSchema = (selected: string): any => {
  yup.object().shape({
    start_date:
      selected === "byDate"
        ? yup.date().max(new Date(), "From Date cannot be in future").required("* Required field")
        : yup.date(),
    end_date:
      selected === "byDate"
        ? yup
            .date()
            .nullable()
            .when("start_date", (start, schema) =>
              start ? schema.min(start, "To Date must be after From Date") : schema,
            )
            .max(new Date(), "From Date cannot be in future")
            .required("* Required field")
        : yup.date(),
  });
};
